import dayjs from 'dayjs';
import * as yup from 'yup';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
dayjs.extend(isSameOrAfter);
import { TFunction } from 'i18next';
import {
  SubmissionResponseDtoTransactionTypeEnum,
  CreateSubmissionPayloadDtoStatusEnum,
  CreateSubmissionPayloadDtoPolicyTypeEnum,
  CreateSubmissionPayloadDto,
  PlacingTypeResponseDtoNameEnum,
} from 'libs/pinnacle-sdk';
import { getLocalDateForMidnightUTC, isUuid } from 'utils';

export const getValidationSchema = (t: TFunction) =>
  yup.object().shape({
    status: yup
      .string()
      .oneOf([
        CreateSubmissionPayloadDtoStatusEnum.Prospect,
        CreateSubmissionPayloadDtoStatusEnum.Pending,
      ])
      .required(t('validation.required', { field: t('submission.status') })),

    policyType: yup
      .string()
      .oneOf(Object.values(CreateSubmissionPayloadDtoPolicyTypeEnum))
      .required(
        t('validation.required', { field: t('submission.policyType') })
      ),

    transactionType: yup
      .string()
      .oneOf(Object.values(SubmissionResponseDtoTransactionTypeEnum))
      .required(
        t('validation.required', { field: t('submission.transactionType') })
      ),

    placingType: yup
      .string()
      .required(
        t('validation.required', { field: t('submission.placementType') })
      ),

    lineOfBusiness: yup
      .string()
      .required(
        t('validation.required', { field: t('submission.lineOfBusiness') })
      ),

    subClass: yup
      .string()
      .required(t('validation.required', { field: t('submission.subClass') })),

    assignedHandlerId: yup
      .string()
      .required(t('validation.required', { field: t('general.underwriter') })),

    namedUnderwriterId: yup
      .string()
      .required(
        t('validation.required', { field: t('submission.namedUnderwriter') })
      ),

    inceptionDate: yup
      .date()
      .required(
        t('validation.required', { field: t('submission.inceptionDate') })
      ),

    expiryDate: yup.date().when('status', {
      is: CreateSubmissionPayloadDtoStatusEnum.Prospect,
      then: () =>
        yup
          .date()
          .nullable()
          .test(
            'is-after-inception',
            t('validation.dateAfterInception', {
              field: t('submission.expiryDate'),
              date: t('submission.inceptionDate'),
            }),
            function (value) {
              if (!value) return true;
              const inceptionDate = this.parent.inceptionDate;
              if (!inceptionDate) return true;
              return dayjs(value).isAfter(
                getLocalDateForMidnightUTC(inceptionDate)
              );
            }
          ),
      otherwise: () =>
        yup
          .date()
          .required(
            t('validation.required', { field: t('submission.expiryDate') })
          )
          .test(
            'is-after-inception',
            t('validation.dateAfterInception', {
              field: t('submission.expiryDate'),
              date: t('submission.inceptionDate'),
            }),
            function (value) {
              const inceptionDate = this.parent.inceptionDate;
              if (!inceptionDate) return true;
              return dayjs(value).isAfter(
                getLocalDateForMidnightUTC(inceptionDate)
              );
            }
          ),
    }),

    insuredId: yup
      .string()
      .required(t('validation.required', { field: t('insured.insured') }))
      .test(
        'is-uuid',
        t('validation.invalid', { field: t('insured.insured') }),
        (value) => isUuid(value)
      ),

    principalOccupation: yup.string().when('status', {
      is: CreateSubmissionPayloadDtoStatusEnum.Prospect,
      then: () => yup.string(),
      otherwise: () =>
        yup.string().required(
          t('validation.required', {
            field: t('submission.principalOccupation'),
          })
        ),
    }),

    originalCurrency: yup.string().when('status', {
      is: CreateSubmissionPayloadDtoStatusEnum.Prospect,
      then: () => yup.string(),
      otherwise: () =>
        yup
          .string()
          .required(t('validation.required', { field: t('general.currency') })),
    }),

    mainRegionAndRiskTerritory: yup.string().when('status', {
      is: CreateSubmissionPayloadDtoStatusEnum.Prospect,
      then: () => yup.string(),
      otherwise: () =>
        yup.string().required(
          t('validation.required', {
            field: t('submission.mainRegionAndRiskTerritory'),
          })
        ),
    }),

    keyRiskLocation: yup.string(),

    comment: yup.string().optional(),
  });

export const initialValues = {
  assignedHandlerId: undefined,
  namedUnderwriterId: undefined,
  inceptionDate: undefined,
  expiryDate: undefined,
  insuredId: undefined,
  principalOccupation: undefined,
  originalCurrency: undefined,
  mainRegionAndRiskTerritory: undefined,
  keyRiskLocation: undefined,
  transactionType: SubmissionResponseDtoTransactionTypeEnum.NewBusiness,
  placingType: PlacingTypeResponseDtoNameEnum.Facultative,
  lineOfBusiness: undefined,
  subClass: undefined,
  status: CreateSubmissionPayloadDtoStatusEnum.Pending,
  policyType: CreateSubmissionPayloadDtoPolicyTypeEnum.Reinsurance,
  comment: undefined,
};

export const fields = Object.keys(
  initialValues
) as (keyof CreateSubmissionPayloadDto)[];

export enum EFormStage {
  SUBMISSION_INFO = 'SUBMISSION_INFO',
  TRANSACTION_DETAILS = 'TRANSACTION_DETAILS',
  REGISTRATION = 'REGISTRATION',
  UPLOAD = 'UPLOAD',
}

const pendingRequiredFields: Record<
  EFormStage,
  (keyof CreateSubmissionPayloadDto)[]
> = {
  [EFormStage.SUBMISSION_INFO]: ['status', 'policyType'],
  [EFormStage.TRANSACTION_DETAILS]: [
    'transactionType',
    'placingType',
    'lineOfBusiness',
    'subClass',
  ],
  [EFormStage.REGISTRATION]: [
    'assignedHandlerId',
    'namedUnderwriterId',
    'inceptionDate',
    'expiryDate',
    'insuredId',
    'principalOccupation',
    'originalCurrency',
    'mainRegionAndRiskTerritory',
  ],
  [EFormStage.UPLOAD]: [],
};

const prospectRequiredFields: Record<
  EFormStage,
  (keyof CreateSubmissionPayloadDto)[]
> = {
  [EFormStage.SUBMISSION_INFO]: ['status', 'policyType'],
  [EFormStage.TRANSACTION_DETAILS]: [
    'transactionType',
    'placingType',
    'lineOfBusiness',
    'subClass',
  ],
  [EFormStage.REGISTRATION]: [
    'assignedHandlerId',
    'namedUnderwriterId',
    'inceptionDate',
    'insuredId',
  ],
  [EFormStage.UPLOAD]: [],
};

export const getStageFields = (
  stage: EFormStage,
  status?: CreateSubmissionPayloadDtoStatusEnum
) => {
  return status === CreateSubmissionPayloadDtoStatusEnum.Prospect
    ? prospectRequiredFields[stage]
    : pendingRequiredFields[stage];
};
